import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { Button, FormControl, TextField, Select, MenuItem, InputLabel } from '@mui/material';
import * as yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { nanoid } from 'nanoid';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import SaveIcon from '@mui/icons-material/Save';
import ModalWindow from '../ModalWindow/ModalWindow';
import { DashboardContext } from '../DashboardContext';
import { localizeErrors } from '../../../utils/helpers';
import { MenuProps } from './consts';

export const AddExpenses = ({
  open, handleClose, selectedCategory, selectedExpense, setEditExpenseButton, editMode, expense
}) => {
  const {
    isFetchingExpenses, expensesErrors,
    fetchEditExpenses, fetchAddExpenses, expenses,
    isFetchingArrivals, arrivalsErrors,
    editArrivalItem, addArrivalItem, arrivals
  } = useContext(DashboardContext);

  const renderCategories = useMemo(() => {
    const items = expense ? expenses : arrivals;
    return items?.map(
      ({ id, title }) => <MenuItem key={nanoid()} value={id}>{title}</MenuItem>);
  }, [expense, expenses, arrivals]);

  const submitForm = useCallback((value, resetForm) => {
    const { title } = value;
    if (expense) {
      if (editMode) {
        fetchEditExpenses({
          id: selectedExpense.id,
          category_id: selectedCategory.id,
          other_expense_id: selectedExpense.id,
          title
        }).then((resp) => {
          if (resp === 'success') {
            handleClose();
            resetForm();
          }
        });
      } else {
        fetchAddExpenses({ category_id: selectedCategory.id, title }).then((resp) => {
          if (resp === 'success') {
            handleClose();
            resetForm();
          }
        });
      }
    } else if (editMode) {
      editArrivalItem({
        id: selectedExpense.id,
        category_id: selectedCategory.id,
        other_expense_id: selectedExpense.id,
        title
      }).then((resp) => {
        if (resp === 'success') {
          handleClose();
          resetForm();
        }
      });
    } else {
      addArrivalItem({ category_id: selectedCategory.id, title }).then((resp) => {
        if (resp === 'success') {
          handleClose();
          resetForm();
        }
      });
    }
  }, [expense, editMode, fetchEditExpenses, selectedCategory, selectedExpense, handleClose,
    fetchAddExpenses, editArrivalItem, addArrivalItem]);

  const formik = useFormik({
    initialValues: {
      title: editMode ? selectedExpense.title : ''
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      title: yup
        .string()
        .max(48, 'Максимально 48 символів')
        .required('Обов\'язково до заповнення')
    }),
    onSubmit: (values, { resetForm }) => {
      submitForm(values, resetForm);
    }
  });

  const onClickReset = () => {
    formik.resetForm();
    handleClose();
  };

  const renderErrors = useMemo(() => {
    const errors = expense ? expensesErrors : arrivalsErrors;
    return errors?.map((item) => <p key={nanoid()} className="errorText">{localizeErrors[item] || item}</p>);
  }, [expense, expensesErrors, arrivalsErrors]);

  return (
    <ModalWindow
      open={open}
      maxWidth="500px"
      onClose={handleClose}
      onBackdropClick={onClickReset}
    >
      <div className="addUsers addPopup">
        <form onSubmit={formik.handleSubmit}>
          <FormControl size="small" fullWidth>
            <TextField
              size="small"
              id="title"
              label="Назва позиції*"
              name="title"
              placeholder="Введіть назву позиції"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
            />
            {renderErrors}
          </FormControl>
          {editMode && (
          <FormControl size="small" fullWidth>
            <InputLabel>Змінити категорію для цієї позиції</InputLabel>
            <Select
              labelId="cityLabel"
              label="Змінити категорію для цієї позиції"
              value={selectedCategory.id}
              MenuProps={MenuProps}
              onChange={(e) => setEditExpenseButton(
                (prev) => ({ ...prev, selectedCategory: { id: e.target.value } }))}
            >
              {renderCategories}
            </Select>
          </FormControl>
          )}
          <div className="addUsers-actions">
            <LoadingButton
              variant="contained"
              loading={expense ? isFetchingExpenses : isFetchingArrivals}
              type="submit"
              disabled={!formik.isValid}
            >
              <SaveIcon sx={{ marginRight: '5px' }} />
              Зберегти
            </LoadingButton>
            <Button type="reset" variant="contained" color="error" onClick={() => onClickReset()}>
              <DisabledByDefaultIcon sx={{ marginRight: '5px' }} />
              Скасувати
            </Button>
          </div>
        </form>
      </div>
    </ModalWindow>
  );
};

AddExpenses.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  setEditExpenseButton: PropTypes.func,
  selectedCategory: PropTypes.object,
  selectedExpense: PropTypes.object,
  editMode: PropTypes.bool,
  expense: PropTypes.bool
};
